/*
 * Overlay chrome for the package's duplicated `Sheet` / `Dialog` (copied from
 * the app's `components/Sheet` + `components/Dialog`). Those use CSS Modules,
 * which this package's tsc-only build can't process — so the same rules live
 * here as a string injected once into <head>. Class names are globally prefixed
 * (`rango-`) to avoid collisions. Everything Radix Themes can express stays as
 * Radix props in the components; only what it has no primitive for is here:
 * bottom-sheet positioning, the top-rounded surface, the column layout, and the
 * open/close animations keyed off Radix's `data-state`.
 *
 * NOTE: temporary duplication — remove once these overlays are shared.
 */
export const overlayClassNames = {
    sheetOverlay: 'rango-sheet-overlay',
    sheetContent: 'rango-sheet-content',
    dialogContent: 'rango-dialog-content',
    dialogContentStatic: 'rango-dialog-content-static',
    dialogBody: 'rango-dialog-body',
};
const STYLE_ELEMENT_ID = 'rango-profile-overlay-styles';
const OVERLAY_CSS = `
/*
 * z-index sits above the app's ModalStack (overlay 1000 / panels 1001): this
 * Sheet is opened ON TOP of a stacked modal (e.g. the intro sheet over the
 * Connect Wallet panel), and both portal into the same container. At 1000 the
 * backdrop would render *under* the stack's panel and fail to dim it, so the
 * overlay goes to 1002 and the content to 1003 (content must stay above its
 * own backdrop).
 */
.${overlayClassNames.sheetOverlay} {
  position: fixed;
  inset: 0;
  z-index: 1002;
  background-color: var(--color-overlay);
}
.${overlayClassNames.sheetOverlay}[data-state='open'] {
  animation: rangoSheetOverlayIn 200ms ease-out;
}
.${overlayClassNames.sheetOverlay}[data-state='closed'] {
  animation: rangoSheetOverlayOut 150ms ease-in;
}
.${overlayClassNames.sheetContent} {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1003;
  max-height: 90vh;
  background-color: var(--color-panel-solid);
  border: 1px solid var(--gray-a5);
  border-top-left-radius: var(--radius-4);
  border-top-right-radius: var(--radius-4);
  box-shadow: var(--shadow-6);
  outline: none;
}
.${overlayClassNames.sheetContent}[data-state='open'] {
  animation: rangoSheetSlideIn 260ms cubic-bezier(0.32, 0.72, 0, 1);
}
.${overlayClassNames.sheetContent}[data-state='closed'] {
  animation: rangoSheetSlideOut 200ms ease-in;
}
@keyframes rangoSheetOverlayIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes rangoSheetOverlayOut {
  from { opacity: 1; }
  to { opacity: 0; }
}
@keyframes rangoSheetSlideIn {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}
@keyframes rangoSheetSlideOut {
  from { transform: translateY(0); }
  to { transform: translateY(100%); }
}
.${overlayClassNames.dialogContent} {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  max-height: 90vh;
  overflow: hidden;
}
.${overlayClassNames.dialogContentStatic} {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  overflow: visible;
}
.${overlayClassNames.dialogBody} {
  flex: 1 1 auto;
  min-height: 0;
}
@media (prefers-reduced-motion: reduce) {
  .${overlayClassNames.sheetOverlay},
  .${overlayClassNames.sheetContent} {
    animation: none;
  }
}
`;
/** Inject the overlay stylesheet into <head> once (idempotent, client-only). */
export function ensureOverlayStyles() {
    if (typeof document === 'undefined' || document.getElementById(STYLE_ELEMENT_ID)) {
        return;
    }
    const style = document.createElement('style');
    style.id = STYLE_ELEMENT_ID;
    style.textContent = OVERLAY_CSS;
    document.head.appendChild(style);
}
